import { useState, useCallback, useEffect, useMemo } from "react";
import { useGetTopics } from "@/api/useGetTopics";
import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import Select from "@/components/shared/select";
import { PlusIcon } from "@radix-ui/react-icons";
import { Loader2Icon } from "lucide-react";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { SubTopic } from "@/v2/entities/subtopic/SubTopic.model";
import { useSubTopicApi } from "@/v2/entities/subtopic/SubTopic.api";
import { SubTopicItem } from "./SubTopicItem";
import { CreateSubTopicDialog } from "./CreateSubTopicDialog";
import styles from "./SubTopics.module.scss";

export function SubTopics() {
  const { data: topics, isLoading: topicsLoading } = useGetTopics();

  const subTopicApi = useSubTopicApi();
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");
  const [subTopics, setSubTopics] = useState<SubTopic[]>([]);
  const [subTopicsLoading, setSubTopicsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const selectedTopicIdNum = useMemo(
    () => (selectedTopicId ? parseInt(selectedTopicId, 10) : null),
    [selectedTopicId]
  );

  const fetchSubTopics = useCallback(async () => {
    if (selectedTopicIdNum == null) {
      setSubTopics([]);
      return;
    }
    setSubTopicsLoading(true);
    try {
      const list = await subTopicApi.getSubTopics(selectedTopicIdNum);
      setSubTopics(list ?? []);
    } catch {
      setSubTopics([]);
    } finally {
      setSubTopicsLoading(false);
    }
  }, [selectedTopicIdNum, subTopicApi]);

  useEffect(() => {
    if (topics?.length && !selectedTopicId) {
      const sorted = [...topics].sort((a: Topic, b: Topic) =>
        a.topic.localeCompare(b.topic)
      );
      setSelectedTopicId(String(sorted[0].id));
    }
  }, [topics, selectedTopicId]);

  useEffect(() => {
    fetchSubTopics();
  }, [fetchSubTopics]);

  const topicOptions = useMemo(
    () =>
      (topics ?? [])
        .slice()
        .sort((a: Topic, b: Topic) => a.topic.localeCompare(b.topic))
        .map((t: Topic) => ({ label: t.topic, value: String(t.id) })),
    [topics]
  );

  const handleTopicChange = useCallback((value: string) => {
    setSelectedTopicId(value);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    fetchSubTopics();
  }, [fetchSubTopics]);

  if (topicsLoading) {
    return (
      <Flex justify="center" align="center" className={styles.loaderContainer}>
        <Loader2Icon className="animate-spin" />
      </Flex>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="div" size="4" weight="medium">
          Sub topics
        </Text>
      </div>

      <div className={styles.content}>
        <Flex direction="column" gap="8">
          <Flex direction="row" gap="2" align="center" justify="between">
            <Flex gap="2" align="center">
              <Select
                options={topicOptions}
                value={selectedTopicId}
                onChange={handleTopicChange}
                placeholder="Select a topic"
                classes={{ root: styles.topicSelect }}
              />
              {selectedTopicId && (
                <Badge color="gray">{subTopics.length}</Badge>
              )}
            </Flex>
            {selectedTopicId && (
              <Button
                size="2"
                highContrast
                onClick={() => setCreateDialogOpen(true)}
              >
                <PlusIcon />
                Create SubTopic
              </Button>
            )}
          </Flex>

          {!selectedTopicId && (
            <Text size="2" color="gray">
              Select a topic to view and manage its subtopics.
            </Text>
          )}

          {selectedTopicId && subTopicsLoading && (
            <Flex justify="center" align="center" py="6">
              <Loader2Icon className="animate-spin" />
            </Flex>
          )}

          {selectedTopicId && !subTopicsLoading && (
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Name
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-[80px] text-right">
                    <Text as="span" size="2" color="gray">
                      Actions
                    </Text>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subTopics.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={2}>
                      <Flex
                        align="center"
                        justify="center"
                        py="6"
                        direction="column"
                        gap="2"
                      >
                        <Text size="2" color="gray">
                          No Sub topics for this topic. Create one to get
                          started.
                        </Text>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                )}
                {subTopics
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((subTopic) => (
                    <SubTopicItem
                      key={subTopic.id}
                      subTopic={subTopic}
                      onSuccess={fetchSubTopics}
                    />
                  ))}
              </Table.Body>
            </Table.Root>
          )}
        </Flex>
      </div>

      {selectedTopicIdNum != null && (
        <CreateSubTopicDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          topicId={selectedTopicIdNum}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
